/**
 * useTaskKanbanColumn - Carrega tarefas de uma coluna do Kanban de Tarefas
 * 
 * Implementa cursor-based pagination com infinite scroll por coluna,
 * seguindo o padrão do useKanbanColumn.ts (CRM).
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-06 v1.0.0 - Criação inicial
 */
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { TaskKanbanFilters, TaskKanbanItem } from './useTaskKanban';

// Logger para depuração
const logger = createLogger('TaskKanbanColumn');

// Tamanho da página
const PAGE_SIZE = 5; // v1.1.0: carga inicial reduzida (20 -> 5) para acelerar o primeiro render

/**
 * Hook para carregar tarefas de uma fase específica do Kanban
 * Utiliza cursor-based pagination (created_at + id)
 * 
 * @param phaseId - ID da fase/coluna
 * @param filters - Filtros opcionais
 * @param enabled - Se deve buscar dados
 */
export function useTaskKanbanColumn(
  phaseId: string | null,
  filters?: TaskKanbanFilters,
  enabled: boolean = true
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey: ['task-kanban-column', phaseId, filters],
    queryFn: async ({ pageParam }) => {
      logger.debug(`Buscando tarefas da fase ${phaseId}, cursor:`, pageParam);

      const { data, error } = await supabase.rpc('get_task_kanban_column', {
        p_phase_id: phaseId!,
        p_limit: PAGE_SIZE,
        p_cursor_created_at: pageParam?.created_at || null,
        p_cursor_id: pageParam?.id || null,
        p_status_filter: (filters?.status as any) || null,
        p_priority_filter: (filters?.priority as any) || null,
        p_assigned_to_filter: filters?.assigned_to || null,
        p_search: filters?.search || null,
        // v1.8.0: Novos filtros avançados
        p_classification_id: filters?.classification_id || null,
        p_created_by_filter: filters?.created_by || null,
        p_due_date_from: filters?.due_date_from || null,
        p_due_date_to: filters?.due_date_to || null,
        p_created_from: filters?.created_from || null,
        p_created_to: filters?.created_to || null,
        p_completed_from: filters?.completed_from || null,
        p_completed_to: filters?.completed_to || null,
        p_tenant_hint: currentTenant?.id || null,
        // v1.1.0: Filtro "Sem Prazo" / "Com Prazo"
        p_due_date_is_null: filters?.due_date_is_null ?? null,
        // v1.11.0: filtros exclusivos de Retenção/Jurídico
        p_request_from: filters?.request_from || null,
        p_request_to: filters?.request_to || null,
        p_period: filters?.period || null,
        p_legal_deadline_from: filters?.legal_deadline_from || null,
        p_legal_deadline_to: filters?.legal_deadline_to || null,
      } as any);

      if (error) {
        logger.error('Erro ao buscar coluna do Kanban de Tarefas:', error);
        throw error;
      }

      logger.debug(`Recebidas ${data?.length || 0} tarefas da fase ${phaseId}`);
      return (data || []) as TaskKanbanItem[];
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      const lastItem = lastPage[lastPage.length - 1];
      return { created_at: lastItem.created_at, id: lastItem.id };
    },
    initialPageParam: null as { created_at: string; id: string } | null,
    enabled: !!phaseId && !!currentTenant?.id && enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 30_000,
  });

  // Invalida cache desta coluna
  const invalidateColumn = () => {
    queryClient.invalidateQueries({ queryKey: ['task-kanban-column', phaseId] });
  };

  // Flatten páginas
  const tasks = query.data?.pages.flat() || [];

  return {
    tasks,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    invalidateColumn,
    error: query.error,
  };
}

/**
 * Hook para invalidar todas as colunas de uma categoria
 */
export function useInvalidateTaskKanbanColumns() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['task-kanban-column'] });
    queryClient.invalidateQueries({ queryKey: ['task-kanban-counts'] });
  };
}
